import React, {useEffect, useState} from 'react';
import {Box, Divider, IconButton, InputLabel, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';

import {
  ExecutionDetailsService,
  executionsService,
  ExecutionStatus,
  ManageExecution,
} from '@store/operations/executions';

import dayjs, {combineDateAndHours} from '@utils/dayjs.utils';

import {Sizes} from '@constants/sizes.constants';
import {AddQuantity, Remove} from '@icons/index';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import SelectComponent from '@components/inputs/select/Select.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import TimeTextFieldComponent from '@components/inputs/timePicker/TimeTextFieldComponent';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import {useNavigate} from 'react-router-dom';
import {AdminRoutes, ClientsRoutes} from '@utils/routes.utils';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import {NumberExt} from '@utils/extensions/number.extension';
import LinkedExecutionModal from '../LinkedExecutionModal';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import {of} from 'rxjs';
import SelectServiceModal from '@screens/admin/operations/executions/manageExecution/SelectService.modal';

interface DetailsFormProps {
  execution: ManageExecution;
  isCreation?: boolean;
  isMainExecution?: boolean;
  readOnly?: boolean;
  updateExecution: (data: ManageExecution, field?: string) => void;
  errors: { [key: string]: any };
}

const DetailsForm = (props: DetailsFormProps) => {
  const {execution, errors, isCreation, isMainExecution, readOnly, updateExecution} = props;

  const navigate = useNavigate();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [services, setServices] = useState<ExecutionDetailsService[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [showEditService, setShowEditService] = useState(false);

  useEffect(() => {
    if (execution.organisationUnit) {
      executionsService.getAllServicesForUnit(execution.organisationUnit.id, !isCreation ? execution.oldService?.supplierOrgUnitId : undefined).subscribe({
        next: setServices,
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, execution.organisationUnit, isCreation, execution.oldService?.supplierOrgUnitId]);

  useEffect(() => {

    if (execution.service && !execution.oldService) {
      const service = services.find((s) => s.id === execution.service);
      updateExecution({oldService: service, oldKind: service?.kind});
    }

  }, [execution.oldService, execution.service, services, updateExecution]);

  const disabled = !execution.service;

  return <>
    <Stack py="20px" spacing={2}>
      <Typography fontSize={16} fontWeight="700">
        {t('operations.executions.manage.details')}
      </Typography>
      <Stack spacing={2}>
        {!!execution.oldService && (
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <InputLabel sx={{width: Sizes.drawerInputLabelWidth}} required={false}>
              {t('operations.executions.manage.serviceDetailLabel')}*
            </InputLabel>
            <IconButton
              onClick={() =>
                navigate(`/${AdminRoutes.CLIENTS}/${ClientsRoutes.SERVICES}/${execution.oldService?.id}`)
              }
            >
              <OpenInNewIcon color="primary"/>
            </IconButton>
          </Stack>
        )}
        <AsyncSelectWithSearchComponent color="secondary"
                                        labelWidth={Sizes.drawerInputLabelWidth}
                                        label={t('operations.executions.manage.service')}
                                        placeholder={t('global.selectOption')}
                                        handleChange={(service) => {
                                          updateExecution({service: service.value});
                                        }}
                                        value={execution.service ? {
                                          value: execution.service || 0,
                                          label: execution.oldService?.clientWasteName || ''
                                        } : undefined}
                                        error={errors.service}
                                        getOptions={(search) => of(services.filter(s => s.clientWasteName.toLowerCase().includes((search || '').toLowerCase())).map((s) => ({
                                          label: s.clientWasteName,
                                          value: s.id,
                                          data: s
                                        })))}
                                        readOnly={readOnly}
                                        required/>
        <TextFieldComponent
          color="secondary"
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('operations.executions.manage.type')}
          placeholder={t('global.selectOption')}
          value={execution.oldKind?.kind || ''}
          disabled={disabled}
          readOnly
        />
        <TextFieldComponent
          color="secondary"
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('operations.executions.manage.wasteType')}
          placeholder={t('global.selectOption')}
          value={execution.oldService?.waste?.name || ''}
          readOnly
        />
        <TextFieldComponent
          color="secondary"
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('operations.executions.manage.container')}
          placeholder={t('global.selectOption')}
          value={execution.oldService?.container?.name || ''}
          readOnly
        />
        {isMainExecution && (
          <>
            <TextFieldComponent
              color="secondary"
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('operations.executions.manage.supplier')}
              placeholder={t('global.selectOption')}
              value={execution.oldService?.supplierOrganisationUnitName}
              readOnly
            />
            <SelectComponent
              color="secondary"
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('operations.executions.manage.status')}
              placeholder={t('global.selectOption')}
              handleChange={(status) => updateExecution({status}, 'status')}
              items={ExecutionStatus.selectItems()}
              value={execution.status || ''}
              error={errors.status}
              disabled={disabled}
              readOnly={readOnly}
              required
            />
            <DatePickerComponent
              color="primary"
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('operations.executions.manage.executedAt')}
              placeholder={t('global.selectDate')}
              handleChange={(executedAt) =>
                updateExecution(
                  {
                    executedAt: executedAt ? dayjs(executedAt).toDate() : undefined,
                  },
                  'plannedDate'
                )
              }
              error={errors.executedAt}
              value={execution.executedAt}
              readOnly={readOnly}
              disabled={disabled || !execution.service}
            />
            <DatePickerComponent
              color="primary"
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('operations.executions.manage.date')}
              placeholder={t('global.selectDate')}
              handleChange={(plannedDate) =>
                updateExecution(
                  {
                    plannedDate: plannedDate,
                    plannedFrom:
                      plannedDate && dayjs(plannedDate).isValid() && execution.plannedFrom
                        ? combineDateAndHours(plannedDate, execution.plannedFrom)
                        : execution.plannedFrom,
                    plannedTo:
                      plannedDate && dayjs(plannedDate).isValid() && execution.plannedTo
                        ? combineDateAndHours(plannedDate, execution.plannedTo)
                        : execution.plannedTo,
                  },
                  'plannedDate'
                )
              }
              error={errors.plannedDate}
              value={execution.plannedDate}
              readOnly={readOnly}
              disabled={disabled || !execution.service}
              required
            />
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
                {t('operations.executions.manage.schedule')}*
              </InputLabel>
              <Box flex={0.5}>
                <TimeTextFieldComponent
                  color="secondary"
                  label={t('global.scheduleFrom')}
                  handleChange={(plannedFrom) =>
                    updateExecution(
                      {
                        plannedFrom:
                          execution.plannedDate && plannedFrom
                            ? combineDateAndHours(execution.plannedDate, plannedFrom)
                            : plannedFrom ?? undefined,
                      },
                      'plannedFrom'
                    )
                  }
                  error={errors.plannedFrom}
                  value={execution.plannedFrom}
                  readOnly={readOnly}
                  disabled={disabled || !execution.service}
                />
              </Box>
              <Box flex={0.5}>
                <TimeTextFieldComponent
                  color="secondary"
                  label={t('global.scheduleTo')}
                  handleChange={(plannedTo) =>
                    updateExecution(
                      {
                        plannedTo:
                          execution.plannedDate && plannedTo
                            ? combineDateAndHours(execution.plannedDate, plannedTo)
                            : plannedTo ?? undefined,
                      },
                      'plannedTo'
                    )
                  }
                  error={errors.plannedTo}
                  value={execution.plannedTo}
                  readOnly={readOnly}
                  disabled={disabled || !execution.service}
                />
              </Box>
            </Stack>
          </>
        )}
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
            {t('operations.executions.manage.containerQuantity')}*
          </InputLabel>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              aria-label="quantity"
              centerRipple={false}
              disabled={readOnly || (execution.quantity1 || 0) <= 1}
              onClick={() => {
                updateExecution({
                  quantity1: (execution.quantity1 || 0) - 1,
                });
              }}
            >
              <Remove/>
            </IconButton>
            <NumberTextFieldComponent
              inputWidth={140}
              color="secondary"
              defaultIfEmpty={1}
              centered
              pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
              handleChange={(value) => {
                updateExecution({quantity1: value});
              }}
              readOnly={readOnly}
              value={execution.quantity1}
              endAdornment={<Typography fontSize={12} fontWeight="400">
                {execution.oldService?.kind.execUnitName1 || ''}
              </Typography>}/>
            <IconButton
              aria-label="quantity"
              centerRipple={false}
              disabled={readOnly}
              onClick={() => {
                updateExecution({
                  quantity1: (execution.quantity1 || 0) + 1,
                });
              }}
            >
              <AddQuantity/>
            </IconButton>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
            {t('operations.executions.manage.wasteQuantity')}
          </InputLabel>
          <NumberTextFieldComponent
            inputWidth={225}
            color="secondary"
            defaultIfEmpty={1}
            centered
            pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
            handleChange={(value) => {
              updateExecution({quantity2: value});
            }}
            readOnly={readOnly}
            value={execution.quantity2 || 0}
            endAdornment={<Typography fontSize={12} fontWeight="400">
              {execution.oldService?.kind.execUnitName2 || ''}
            </Typography>}
          />
        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
            {t('operations.executions.manage.co2')}
          </InputLabel>
          <TextFieldComponent
            inputWidth={225}
            color="secondary"
            readOnly={true}
            value={NumberExt.round(execution.co2 || 0, 3)}
            endAdornment={<Typography fontSize={12} fontWeight="400">
              TON
            </Typography>}
          />
        </Stack>
        {/*{isMainExecution && !readOnly && (*/}
        {/*  <>*/}
        {/*    <TextFieldComponent*/}
        {/*      color="secondary"*/}
        {/*      labelWidth={Sizes.drawerInputLabelWidth}*/}
        {/*      label={t('operations.executions.manage.haulier')}*/}
        {/*      placeholder={t('global.selectOption')}*/}
        {/*      value={haulingAndDestination?.haulingOrganisationUnitName || ''}*/}
        {/*      readOnly*/}
        {/*    />*/}
        {/*    <TextFieldComponent*/}
        {/*      color="secondary"*/}
        {/*      labelWidth={Sizes.drawerInputLabelWidth}*/}
        {/*      label={t('operations.executions.manage.destination')}*/}
        {/*      placeholder={t('global.selectOption')}*/}
        {/*      value={haulingAndDestination?.destinationOrganisationUnitName || ''}*/}
        {/*      readOnly*/}
        {/*    />*/}
        {/*  </>*/}
        {/*)}*/}
        <TextFieldComponent
          color="secondary"
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('operations.executions.manage.trackingDocument')}
          handleChange={(value) => {
            updateExecution({additionalInformation: value});
          }}
          value={execution.additionalInformation || ''}
        />
        {!isCreation && (
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <InputLabel sx={{width: Sizes.drawerInputLabelWidth}} required={false}>
              {t('operations.executions.manage.linkedExecutions')}*
            </InputLabel>
            <IconButton
              onClick={() => setOpenModal(true)} // TODO: Display modal with executions linked
            >
              <OpenInNewIcon color="primary"/>
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Stack>
    <Divider variant="fullWidth" orientation="horizontal"/>
    {showEditService && <SelectServiceModal services={services} handleClose={() => setShowEditService(false)}/>}
    {openModal && execution.id ? (
      <LinkedExecutionModal
        executionId={execution.id}
        parentExecutionId={execution.parentExecutionId}
        handleClose={() => setOpenModal(false)}
      />
    ) : undefined}
  </>;
};

export default DetailsForm;
